/**
 * Ant Designのmessageモジュールを使用して、成功やエラーの通知を表示します。
 * @date 2025/6/25
 */
import { message } from 'antd';
import React from 'react';
import { NavigateFunction, useNavigate } from "react-router-dom";
import { LoginAccountState, useLoginAccount } from "src/globalStates/loginAccount";
import { useMessageTemplateRepository } from "src/repositories/messageTemplate/repository"
import { useStorageRepository } from "src/repositories/storage/repository";
import { useMessageTemplateCacheMutator } from "./cache";
import { StorageRepository } from 'src/repositories/storage/interface';
import { MessageTemplateRepository } from 'src/repositories/messageTemplate/interface';
import { MessageTemplate, MessageTemplateFormType } from 'src/models/messageTemplate';

/**
 * メッセージテンプレートフォームのロジックを管理するカスタムフック。
 * 依存関係を初期化し、メモ化されたフォームコンストラクタを返します。
 */
export const useMessageTemplateForm = () => {
  const repository = useMessageTemplateRepository();
  const storageRepo = useStorageRepository();
  const navigator = useNavigate();
  const account = useLoginAccount();
  const mutator = useMessageTemplateCacheMutator();

  return React.useMemo(
    () =>
      createMessageTemplateFormConstructor({
        repository,
        storageRepo,
        navigator,
        account,
        mutator,
      }),
    [repository, storageRepo, navigator, account, mutator]
  );
}

/**
 * メッセージテンプレートフォームのハンドラを作成するファクトリ関数。
 * フォーム送信やエラー処理のロジックをカプセル化します。
 */
function createMessageTemplateFormConstructor({
  repository,
  storageRepo,
  navigator,
  account,
  mutator,
}: {
  repository: MessageTemplateRepository;
  storageRepo: StorageRepository;
  navigator: NavigateFunction;
  account: LoginAccountState;
  mutator: ReturnType<typeof useMessageTemplateCacheMutator>;
}) {
  return {
    /**
     * メッセージテンプレートフォームデータを送信します。
     * @param value - 送信するフォームデータ。
     * @param image - オプションの画像ファイル。
     */
    async submit(
      value: MessageTemplateFormType,
      image?: File
    ) {
      try {
        // usageが未選択の場合
        if (value.usage === undefined) {
          message.error('使用目的を選択してください');
          return;
        }

        // DMの場合カテゴリとテキストを確認
        if (value.usage === 'directMessage') {
          // カテゴリが未選択の場合
          if (value.category === undefined) {
            message.error('カテゴリを選択してください');
            return;
          }
          // テキストが未入力の場合
          if (value.text === undefined || value.text === '') {
            message.error('テキストを入力してください');
            return;
          }
        }

        let _imageUrl: string | null = null;

        // 配信の場合タイトル、カテゴリ、送信内容タイプを確認
        if (value.usage === 'broadcast') {
          // タイトルが未入力の場合
          if (value.title === undefined || value.title === '') {
            message.error('タイトルを入力してください');
            return;
          }

          // カテゴリが未選択の場合
          if (value.category === undefined) {
            message.error('カテゴリを選択してください');
            return;
          }

          // 送信内容タイプが未選択の場合
          if (value.type === undefined) {
            message.error('送信内容タイプを選択してください');
            return;
          }

          const _needImage =
            value.type === 'image' || value.type === 'textAndImage';

          const _needTempImage =
            value.type === 'template' || value.type === 'textAndTemplate';

          _imageUrl = _needImage
            ? value.messageTemplate?.broadCastMessage?.imageUrl ?? null
            : _needTempImage
              ? value.messageTemplate?.broadCastMessage?.tempImageUrl ?? null
              : null;

          if (image) {
            _imageUrl = await storageRepo.upload({
              file: image!,
              fileName: image!.name,
              collPath: 'MessageTemplates',
            });
          } else {
            if (_needImage) {
              if (!value.messageTemplate?.broadCastMessage?.imageUrl) {
                message.error('画像をアップロードしてください');
                return;
              }
              _imageUrl = value.messageTemplate?.broadCastMessage!.imageUrl;
            }
            if (_needTempImage) {
              if (!value.messageTemplate?.broadCastMessage?.tempImageUrl) {
                message.error('画像をアップロードしてください');
                return;
              }
              _imageUrl = value.messageTemplate?.broadCastMessage?.tempImageUrl;
            }
          }
        }

        // メッセージテンプレートデータを作成
        const _broadCastMessage = value.usage === 'broadcast' ? {
          type: value.type!,
          altText: value.type === 'template' || value.type === 'textAndTemplate'
            ? value.altText
            : null,
          imageUrl: value.type === 'image' || value.type === 'textAndImage'
            ? _imageUrl
            : null,
          tempImageUrl: value.type === 'template' || value.type === 'textAndTemplate'
            ? _imageUrl
            : null,
          tempLabel: value.type === 'template' || value.type === 'textAndTemplate'
            ? value.tempLabel
            : null,
          tempUri: value.type === 'template' || value.type === 'textAndTemplate'
            ? value.tempUri
            : null,
          text: value.type === 'text' ||
            value.type === 'textAndImage' ||
            value.type === 'textAndTemplate'
            ? value.text
            : null,
        } : {
          type: 'text' as const,
          altText: null,
          imageUrl: null,
          tempImageUrl: null,
          tempLabel: null,
          tempUri: null,
          text: value.usage === 'directMessage' ? value.text : null,
        };

        if (!!!value.messageTemplate) {
          // 新規作成
          const _messageTemplate: MessageTemplate = {
            title: value.usage === 'broadcast' ? value.title : null,
            category: value.category!,
            usage: value.usage,
            broadCastMessage: _broadCastMessage,
            createdAt: new Date(),
            updatedAt: new Date(),
            deletedAt: null,
            documentStatus: 'active',
          };

          await repository.createDoc(_messageTemplate, account);
          mutator.mutateList({ account });
          message.success('新規作成しました');
        } else {
          // 更新
          const _messageTemplate: MessageTemplate = {
            ...value.messageTemplate,
            title: value.usage === 'broadcast' ? value.title : null,
            category: value.category!,
            usage: value.usage,
            broadCastMessage: _broadCastMessage,
            updatedAt: new Date(),
          };

          await repository.updateDoc(_messageTemplate, account);
          mutator.mutateItem(
            { messageTemplateId: value.messageTemplate?.id },
            { messageTemplate: value },
            true
          );
          message.success('更新しました');
        }

        navigator('../');
      }
      catch (error: any) {
        message.error(error?.toString() ?? 'エラーが発生しました');
        return;
      }
    },

    /**
     * メッセージテンプレートを論理削除します。
     * @param value - 削除するテンプレートのデータ
     */
    async deleteMessageTemplate(value: MessageTemplate) {
      try {
        // メッセージテンプレートを更新
        await repository.deleteDoc(value, account);

        mutator.mutateList({ account });
        message.success('テンプレートを削除しました');
      } catch (error: any) {
        message.error('テンプレートの削除に失敗しました');
        return;
      }
    }
  };
}